import type { ReactNode } from 'react';
import { useState } from 'react';
import { Drawer } from 'antd';
import {
  CheckCircleOutlined,
  CloudOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  LockOutlined,
  PlusOutlined,
  RobotOutlined,
  SearchOutlined,
  StopOutlined,
  TeamOutlined,
  UnlockOutlined,
  WifiOutlined,
} from '@ant-design/icons';

import { Bento, DataTable, MenuThaoTac, StatTile, Tile } from '../../../../components/shared';
import { Button, Empty, Input, Select, Skeleton } from '../../../../components/ui';
import { useLoi } from '../../../../hooks/useLoi';
import { useT } from '../../../../i18n';
import type { KhoaDich } from '../../../../i18n';
import { KHONG_CO, ngayGioVN, soVN } from '../../../../lib/dinh-dang';
import { useToast } from '../../../../lib/toast';
import { HopXacNhanThaoTac } from '../../../nguoi-dung/components/HopXacNhanThaoTac';
import { LoiRpa } from '../chung';
import { useKhoaUserOpenCore, useRolesOpenCore, useUsersOpenCore, useXoaUserOpenCore } from '../../hooks/useDanhBaOpenCore';
import { KHONG_ROLE, LOC_TRANG_THAI_USER, locUser, luaChonRole, type BoLocUser } from '../../nghiep-vu/danh-ba';
import type { UserOpenCore } from '../../types';
import { HopUser } from './HopUser';
import { BadgeCoChua, BadgeTrangThaiUser, TheRole, ThoiGianTruoc } from './nhan';

const LOC_GOC: BoLocUser = { tim: '', status: '', validated: '', role: '' };
const NHAN_LOC: Record<(typeof LOC_TRANG_THAI_USER)[number], KhoaDich> = {
  enabled: 'rpaDanhBa.loc.enabled',
  disabled: 'rpaDanhBa.loc.disabled',
  online: 'rpaDanhBa.loc.online',
  rpa: 'rpaDanhBa.loc.rpa',
  unvalidated: 'rpaDanhBa.loc.unvalidated',
};

type Hop = { kieu: 'tao' } | { kieu: 'sua' | 'khoa' | 'xoa'; user: UserOpenCore } | null;

/**
 * Tab User của màn User OpenCore: User của tenant đọc thẳng từ OpenCore (tối đa 500), lọc ở máy khách như native.
 * Sửa/khoá/xoá theo `rpa:opencore-user:update`; gán role thêm `rpa:opencore-role:update`.
 */
export function TabUser({ suaDuoc, ganRoleDuoc, docRoleDuoc }: { suaDuoc: boolean; ganRoleDuoc: boolean; docRoleDuoc: boolean }) {
  const q = useUsersOpenCore();
  if (q.isPending) {
    return (
      <div className="space-y-4" aria-busy="true">
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }
  if (q.isError) {
    return (
      <Tile>
        <LoiRpa loi={q.error} onThuLai={() => void q.refetch()} />
      </Tile>
    );
  }
  return <NoiDung users={q.data.data} suaDuoc={suaDuoc} ganRoleDuoc={ganRoleDuoc} docRoleDuoc={docRoleDuoc} />;
}

function NoiDung({ users, suaDuoc, ganRoleDuoc, docRoleDuoc }: { users: UserOpenCore[]; suaDuoc: boolean; ganRoleDuoc: boolean; docRoleDuoc: boolean }) {
  const { t, locale } = useT();
  // Danh sách role cho bộ lọc: máy chủ cho đọc bằng role:read hoặc user:update.
  const roles = useRolesOpenCore(docRoleDuoc || suaDuoc);
  const [loc, setLoc] = useState<BoLocUser>(LOC_GOC);
  const [hop, setHop] = useState<Hop>(null);
  const [dangXem, setDangXem] = useState<string | null>(null);
  // Đọc lại từ danh sách để ngăn thấy ngay trạng thái mới sau khi khoá / sửa.
  const xem = dangXem ? users.find((u) => u.id === dangXem) : undefined;
  const loc1 = locUser(users, loc);
  const dangLoc = loc.tim.trim() !== '' || loc.status !== '' || loc.validated !== '' || loc.role !== '';
  const dat = (thay: Partial<BoLocUser>) => setLoc((c) => ({ ...c, ...thay }));
  const lyDoBaoVe = (u: UserOpenCore) => (u.protected ? t(u.protected === 'service' ? 'rpaDanhBa.baoVe.service' : 'rpaDanhBa.baoVe.self') : '');

  return (
    <div className="space-y-4">
      <Bento>
        <StatTile span="sm:col-span-6 lg:col-span-2" nhan={t('rpaDanhBa.kpiTong')} so={soVN(users.length, locale)} icon={<TeamOutlined />} tone="primary" />
        <StatTile
          span="sm:col-span-6 lg:col-span-2"
          nhan={t('rpaDanhBa.kpiEnabled')}
          so={soVN(users.filter((u) => !u.disabled).length, locale)}
          icon={<CheckCircleOutlined />}
          tone="success"
        />
        <StatTile
          span="sm:col-span-6 lg:col-span-3"
          nhan={t('rpaDanhBa.kpiOnline')}
          so={soVN(users.filter((u) => u.online).length, locale)}
          icon={<WifiOutlined />}
          tone="sky"
        />
        <StatTile
          span="sm:col-span-6 lg:col-span-2"
          nhan={t('rpaDanhBa.kpiDisabled')}
          so={soVN(users.filter((u) => u.disabled).length, locale)}
          icon={<StopOutlined />}
          tone="danger"
        />
        <StatTile
          span="sm:col-span-12 lg:col-span-3"
          nhan={t('rpaDanhBa.kpiRpa')}
          so={soVN(users.filter((u) => u.rpaHeartbeat || u.openRpaClientVersion).length, locale)}
          icon={<RobotOutlined />}
          tone="violet"
        />
      </Bento>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <Input
            allowClear
            autoComplete="off"
            spellCheck={false}
            prefix={<SearchOutlined aria-hidden />}
            placeholder={t('rpaDanhBa.timUser')}
            aria-label={t('rpaDanhBa.timUser')}
            value={loc.tim}
            onChange={(e) => dat({ tim: e.target.value })}
            className="w-full sm:w-72"
          />
          <Select<string>
            value={loc.status}
            onChange={(status) => dat({ status })}
            aria-label={t('rpaDanhBa.locTrangThai')}
            className="w-52"
            options={[{ value: '', label: t('rpaDanhBa.moiTrangThai') }, ...LOC_TRANG_THAI_USER.map((s) => ({ value: s, label: t(NHAN_LOC[s]) }))]}
          />
          <Select<string>
            value={loc.validated}
            onChange={(validated) => dat({ validated })}
            aria-label={t('rpaDanhBa.locValidated')}
            className="w-44"
            options={[
              { value: '', label: t('rpaDanhBa.moiValidated') },
              { value: 'yes', label: t('rpaDanhBa.daValidated') },
              { value: 'no', label: t('rpaDanhBa.chuaValidated') },
            ]}
          />
          <Select<string>
            value={loc.role}
            onChange={(role) => dat({ role })}
            showSearch
            optionFilterProp="label"
            loading={roles.isPending && (docRoleDuoc || suaDuoc)}
            aria-label={t('rpaDanhBa.locRole')}
            className="w-52"
            options={[
              { value: '', label: t('rpaDanhBa.moiRole') },
              { value: KHONG_ROLE, label: t('rpaDanhBa.chuaCoRole') },
              ...luaChonRole(roles.data, users).map((r) => ({ value: r.id, label: r.name })),
            ]}
          />
          {dangLoc && (
            <Button variant="ghost" size="sm" onClick={() => setLoc(LOC_GOC)}>
              {t('chung.xoaBoLoc')}
            </Button>
          )}
        </div>
        {suaDuoc && (
          <Button variant="primary" size="md" icon={<PlusOutlined />} onClick={() => setHop({ kieu: 'tao' })}>
            {t('rpaDanhBa.taoUser')}
          </Button>
        )}
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-content-muted">
        <span role="status">{t('rpaDanhBa.daTaiUser', { so: soVN(users.length, locale) })}</span>
        <span className="inline-flex items-center gap-1">
          <CloudOutlined aria-hidden /> {t('rpaDanhBa.nguon')}
        </span>
      </div>

      <Tile className="min-w-0">
        {users.length === 0 ? (
          <Empty
            icon={TeamOutlined}
            title={t('rpaDanhBa.rongUser')}
            description={t('rpaDanhBa.rongUserMoTa')}
            action={
              suaDuoc ? (
                <Button variant="primary" size="sm" icon={<PlusOutlined />} onClick={() => setHop({ kieu: 'tao' })}>
                  {t('rpaDanhBa.taoUser')}
                </Button>
              ) : undefined
            }
          />
        ) : loc1.length === 0 ? (
          <Empty icon={SearchOutlined} title={t('rpaDanhBa.rongLoc')} />
        ) : (
          <DataTable<UserOpenCore>
            caption={t('rpaDanhBa.tabUser')}
            dataSource={loc1}
            rowKey="id"
            pagination={loc1.length > 20 ? { pageSize: 20, showSizeChanger: false } : false}
            columns={[
              { title: t('chung.stt'), key: 'stt', width: 56, render: (_, _u, i) => <span className="tabular-nums">{soVN(i + 1, locale)}</span> },
              {
                title: t('rpaDanhBa.cotUser'),
                key: 'user',
                render: (_, u) => (
                  <div className="min-w-0 max-w-[16rem] leading-tight">
                    <p className="truncate font-medium text-content-primary">{u.name || KHONG_CO}</p>
                    <p className="truncate text-xs text-content-secondary">{u.username || KHONG_CO}</p>
                    {u.email && <p className="truncate text-xs text-content-muted">{u.email}</p>}
                  </div>
                ),
              },
              {
                title: t('rpaDanhBa.cotId'),
                key: 'id',
                render: (_, u) => <code className="whitespace-nowrap font-mono text-2xs text-content-muted">{u.id}</code>,
              },
              {
                title: t('rpaDanhBa.cotRole'),
                key: 'roles',
                render: (_, u) =>
                  u.rolesStatus === 'unavailable' ? (
                    <span className="text-xs text-content-muted">{t('rpaDanhBa.roleKhongDoc')}</span>
                  ) : (
                    <TheRole roles={u.roles} />
                  ),
              },
              { title: t('rpaDanhBa.cotTrangThai'), key: 'status', render: (_, u) => <BadgeTrangThaiUser user={u} /> },
              { title: t('rpaDanhBa.cotValidated'), key: 'validated', render: (_, u) => <BadgeCoChua co={u.validated} /> },
              { title: t('rpaDanhBa.cotEmailValidated'), key: 'emailValidated', render: (_, u) => <BadgeCoChua co={u.emailValidated} /> },
              { title: t('rpaDanhBa.cotLastSeen'), key: 'lastSeen', render: (_, u) => <ThoiGianTruoc iso={u.lastSeen} /> },
              { title: t('rpaDanhBa.cotRpaHeartbeat'), key: 'rpaHeartbeat', render: (_, u) => <ThoiGianTruoc iso={u.rpaHeartbeat} /> },
              {
                title: t('rpaDanhBa.cotPhienBan'),
                key: 'version',
                render: (_, u) => (
                  <div className="whitespace-nowrap text-xs leading-tight">
                    <p>{t('rpaDanhBa.openRpaVer', { ver: u.openRpaClientVersion || KHONG_CO })}</p>
                    <p className="text-content-muted">{t('rpaDanhBa.clientVer', { ver: u.clientVersion || KHONG_CO })}</p>
                  </div>
                ),
              },
              {
                title: <span className="sr-only">{t('chung.thaoTac')}</span>,
                key: 'thaoTac',
                width: 72,
                align: 'center',
                fixed: 'right',
                render: (_, u) => {
                  const khoaDichVu = !u.disabled && u.protected === 'service';
                  return (
                    <MenuThaoTac
                      tenBanGhi={u.name || u.username}
                      muc={[
                        { khoa: 'xem', nhan: t('rpaDanhBa.xem'), icon: <EyeOutlined />, nhom: 'xem', onChon: () => setDangXem(u.id) },
                        { khoa: 'sua', nhan: t('rpaDanhBa.sua'), icon: <EditOutlined />, nhom: 'sua', hien: suaDuoc, onChon: () => setHop({ kieu: 'sua', user: u }) },
                        {
                          khoa: 'khoa',
                          nhan: u.disabled ? t('rpaDanhBa.moKhoa') : t('rpaDanhBa.khoa'),
                          icon: u.disabled ? <UnlockOutlined /> : <LockOutlined />,
                          nhom: 'sua',
                          hien: suaDuoc,
                          voHieu: khoaDichVu,
                          ...(khoaDichVu ? { moTa: t('rpaDanhBa.baoVe.service') } : {}),
                          onChon: () => setHop({ kieu: 'khoa', user: u }),
                        },
                        {
                          khoa: 'xoa',
                          nhan: t('rpaDanhBa.xoaUser'),
                          icon: <DeleteOutlined />,
                          nhom: 'pha-huy',
                          hien: suaDuoc,
                          voHieu: Boolean(u.protected),
                          ...(u.protected ? { moTa: lyDoBaoVe(u) } : {}),
                          onChon: () => setHop({ kieu: 'xoa', user: u }),
                        },
                      ]}
                    />
                  );
                },
              },
            ]}
          />
        )}
      </Tile>

      {(hop?.kieu === 'tao' || hop?.kieu === 'sua') && (
        <HopUser goc={hop.kieu === 'sua' ? hop.user : null} ganRoleDuoc={ganRoleDuoc} onClose={() => setHop(null)} />
      )}
      {(hop?.kieu === 'khoa' || hop?.kieu === 'xoa') && <HopXacNhanUser kieu={hop.kieu} user={hop.user} onClose={() => setHop(null)} />}
      {xem && (
        <NganUser
          user={xem}
          suaDuoc={suaDuoc}
          onClose={() => setDangXem(null)}
          onSua={() => {
            setDangXem(null);
            setHop({ kieu: 'sua', user: xem });
          }}
          onKhoa={() => setHop({ kieu: 'khoa', user: xem })}
        />
      )}
    </div>
  );
}

function HopXacNhanUser({ kieu, user: u, onClose }: { kieu: 'khoa' | 'xoa'; user: UserOpenCore; onClose: () => void }) {
  const { t } = useT();
  const toast = useToast();
  const dichLoi = useLoi();
  const khoa = useKhoaUserOpenCore();
  const xoa = useXoaUserOpenCore();
  const ten = u.name || u.username || u.id;
  const dang = kieu === 'xoa' ? xoa : khoa;
  const moKhoa = kieu === 'khoa' && u.disabled;
  return (
    <HopXacNhanThaoTac
      open
      nguyHiem={!moKhoa}
      tieuDe={t(kieu === 'xoa' ? 'rpaDanhBa.xacNhan.xoaTieuDe' : moKhoa ? 'rpaDanhBa.xacNhan.moKhoaTieuDe' : 'rpaDanhBa.xacNhan.khoaTieuDe', { ten })}
      than={t(kieu === 'xoa' ? 'rpaDanhBa.xacNhan.xoaThan' : moKhoa ? 'rpaDanhBa.xacNhan.moKhoaThan' : 'rpaDanhBa.xacNhan.khoaThan', { ten })}
      nhanXacNhan={t(kieu === 'xoa' ? 'rpaDanhBa.xoaUser' : moKhoa ? 'rpaDanhBa.moKhoa' : 'rpaDanhBa.khoa')}
      dangChay={dang.isPending}
      loi={dang.error ? dichLoi(dang.error) : null}
      onClose={onClose}
      onXacNhan={() => {
        const xong = () => {
          toast.success(t(kieu === 'xoa' ? 'rpaDanhBa.xacNhan.daXoa' : moKhoa ? 'rpaDanhBa.xacNhan.daMoKhoa' : 'rpaDanhBa.xacNhan.daKhoa', { ten }));
          onClose();
        };
        if (kieu === 'xoa') xoa.mutate(u.id, { onSuccess: xong });
        else khoa.mutate({ id: u.id, khoa: !u.disabled }, { onSuccess: xong });
      }}
    />
  );
}

/** Chi tiết User (`OpenCoreUserDetailDrawer` của native). */
function NganUser({
  user: u,
  suaDuoc,
  onClose,
  onSua,
  onKhoa,
}: {
  user: UserOpenCore;
  suaDuoc: boolean;
  onClose: () => void;
  onSua: () => void;
  onKhoa: () => void;
}) {
  const { t, locale } = useT();
  const khoaDichVu = !u.disabled && u.protected === 'service';
  return (
    <Drawer
      open
      onClose={onClose}
      placement="right"
      size="min(560px, 100vw)"
      destroyOnHidden
      title={u.name || u.username}
      footer={
        suaDuoc ? (
          <div className="flex flex-wrap justify-end gap-2">
            {!khoaDichVu && (
              <Button variant="outline" size="md" icon={u.disabled ? <UnlockOutlined /> : <LockOutlined />} onClick={onKhoa}>
                {u.disabled ? t('rpaDanhBa.moKhoa') : t('rpaDanhBa.khoa')}
              </Button>
            )}
            <Button variant="primary" size="md" icon={<EditOutlined />} onClick={onSua}>
              {t('rpaDanhBa.sua')}
            </Button>
          </div>
        ) : undefined
      }
    >
      {u.protected && <p className="mb-4 text-xs text-content-muted">{t(u.protected === 'service' ? 'rpaDanhBa.baoVe.service' : 'rpaDanhBa.baoVe.self')}</p>}
      <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Dong nhan={t('rpaDanhBa.form.username')}>{u.username}</Dong>
        <Dong nhan={t('rpaDanhBa.form.email')}>{u.email}</Dong>
        <Dong nhan={t('rpaDanhBa.cotId')}>
          <code className="break-all text-xs">{u.id}</code>
        </Dong>
        <Dong nhan={t('rpaDanhBa.cotTrangThai')}>
          <BadgeTrangThaiUser user={u} />
        </Dong>
        <Dong nhan={t('rpaDanhBa.cotValidated')}>
          <BadgeCoChua co={u.validated} />
        </Dong>
        <Dong nhan={t('rpaDanhBa.cotEmailValidated')}>
          <BadgeCoChua co={u.emailValidated} />
        </Dong>
        <Dong nhan={t('rpaDanhBa.cotLastSeen')}>{ngayGioVN(u.lastSeen, locale)}</Dong>
        <Dong nhan={t('rpaDanhBa.cotRpaHeartbeat')}>{ngayGioVN(u.rpaHeartbeat, locale)}</Dong>
        <Dong nhan={t('rpaDanhBa.ngan.openRpa')}>{u.openRpaClientVersion}</Dong>
        <Dong nhan={t('rpaDanhBa.ngan.client')}>{u.clientVersion}</Dong>
        <Dong nhan={t('rpaDanhBa.ngan.taoLuc')}>{ngayGioVN(u.createdAt, locale)}</Dong>
        <Dong nhan={t('rpaDanhBa.ngan.suaLuc')}>{ngayGioVN(u.modifiedAt, locale)}</Dong>
      </dl>
      <h3 className="mb-2 mt-5 text-xs font-semibold uppercase tracking-wide text-content-muted">{t('rpaDanhBa.ngan.roleTrucTiep')}</h3>
      {u.rolesStatus === 'unavailable' ? (
        <p className="text-sm text-warning-700">{t('rpaDanhBa.roleKhongDoc')}</p>
      ) : u.roles.length ? (
        <TheRole roles={u.roles} toiDa={50} />
      ) : (
        <p className="text-sm text-content-muted">{t('rpaDanhBa.ngan.khongCoRole')}</p>
      )}
    </Drawer>
  );
}

function Dong({ nhan, children }: { nhan: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-content-muted">{nhan}</dt>
      <dd className="break-words text-sm text-content-primary">{children || KHONG_CO}</dd>
    </div>
  );
}
